import { Alert, Box, Link as MuiLink, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "i18n-fe";

// #3646 -- the one-line "Station exception" the Score Admin dashboard shows
// (Jaybill, 2026-10-22: this area is for errors and exceptions ONLY, every
// message is ONE line that never wraps, and it links to the Station Monitor
// for detail). The text is noWrap with an ellipsis, the full text is in the
// `title` tooltip, and the line ends in an "Open station monitor" link.
//
// Reusable on purpose: the dashboard strip's counts line and the compact form
// of every Station Monitor banner render through this one element, and #3647
// puts per-station exceptions on the station table rows with it too.
//
//   text       the full plain-text message (tooltip, and the visible text unless
//              `children` is given)
//   children   optional rich visible text (e.g. spans with their own test ids);
//              it must read the same as `text`
//   extraLink  optional { to, label, testId }: a fix link shown before the
//              station monitor link (e.g. "Add it in Judge Manager."); hidden on
//              phone widths, where only the short monitor link fits the line
export default function StationExceptionLine({
  text,
  children,
  severity = "warning",
  variant = "outlined",
  testId,
  extraLink,
  linkTestId = "station-exception-link",
}) {
  const { t } = useTranslation("scoreadmin");
  return (
    <Alert
      severity={severity}
      variant={variant}
      role="alert"
      data-testid={testId}
      icon={false}
      sx={{
        mb: 1,
        py: 0,
        px: 1.5,
        "& .MuiAlert-message": {
          flex: 1,
          minWidth: 0,
          py: 0.75,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          overflow: "hidden",
        },
      }}
    >
      <Typography variant="body2" noWrap title={text} data-testid="station-exception-text" sx={{ flex: 1, minWidth: 0 }}>
        {children ?? text}
      </Typography>
      {extraLink && (
        <MuiLink
          component={RouterLink}
          to={extraLink.to}
          variant="body2"
          noWrap
          data-testid={extraLink.testId}
          sx={{ flexShrink: 0, color: "inherit", display: { xs: "none", sm: "inline" } }}
        >
          {extraLink.label}
        </MuiLink>
      )}
      <MuiLink
        component={RouterLink}
        to="/station-monitor"
        variant="body2"
        noWrap
        data-testid={linkTestId}
        sx={{ flexShrink: 0, color: "inherit" }}
      >
        {/* Narrow screens get the short label so the message keeps room (#3646 AC7). */}
        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
          {t("stationMonitor.strip.link")}
        </Box>
        <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
          {t("stationMonitor.strip.linkShort")}
        </Box>
      </MuiLink>
    </Alert>
  );
}
